import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { RangeSlider } from '@/components/ui/RangeSlider';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';
import {
  CROP_MAX_SOURCE_BYTES,
  CROP_MAX_SOURCE_PIXELS,
  CROP_ZOOM,
  type ImageCropSpec,
} from '@/lib/constants/image-crop';
import {
  cropImageToFile,
  exceedsPixelLimit,
  loadImageElement,
  type PixelCrop,
} from '@/lib/utils/image-crop.utils';

const S = COMMON_STRINGS.IMAGE_CROP;

export interface ImageCropDialogProps {
  /** The PNG or JPEG the member picked. Callers never open the dialog for anything else. */
  file: File;
  /** The locked frame for this upload kind. */
  spec: ImageCropSpec;
  /** Receives the cropped file, which the caller then uploads through its existing flow. */
  onConfirm: (cropped: File) => void;
  /** Closes without a result; the caller uploads nothing and keeps the previous image. */
  onCancel: () => void;
}

type CropState =
  | { status: 'loading' }
  | { status: 'ready'; src: string; image: HTMLImageElement }
  | { status: 'error'; message: string };

/**
 * Decodes the picked file once and exposes the object URL react-easy-crop shows.
 * An oversized or undecodable file ends in an `error` state with a plain message.
 */
function useDecodedImage(file: File): CropState {
  const [state, setState] = useState<CropState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ status: 'loading' });

    async function decode(): Promise<void> {
      if (file.size > CROP_MAX_SOURCE_BYTES) {
        setState({ status: 'error', message: S.TOO_BIG });
        return;
      }
      objectUrl = URL.createObjectURL(file);
      try {
        const image = await loadImageElement(objectUrl);
        if (cancelled) return;
        if (exceedsPixelLimit(image.naturalWidth, image.naturalHeight, CROP_MAX_SOURCE_PIXELS)) {
          setState({ status: 'error', message: S.TOO_BIG });
          return;
        }
        setState({ status: 'ready', src: objectUrl, image });
      } catch {
        if (!cancelled) setState({ status: 'error', message: S.UNREADABLE });
      }
    }
    void decode();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return state;
}

/**
 * Crop-and-resize step shown between choosing an image and uploading it (FR-121).
 *
 * The frame's shape is locked by `spec`; the member moves and zooms the image under
 * it. Confirming writes the chosen area at the spec's output size (never scaled up)
 * and hands the new file back; cancelling hands back nothing. The upload itself, its
 * validation and its errors stay with the caller's existing flow.
 */
export function ImageCropDialog({ file, spec, onConfirm, onCancel }: ImageCropDialogProps) {
  const decoded = useDecodedImage(file);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(CROP_ZOOM.MIN);
  const [area, setArea] = useState<PixelCrop | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // False once the dialog has closed. A crop that finishes after that must be dropped, or a
  // member who cancelled would still get an upload (FR-121: cancel uploads nothing).
  const isOpenRef = useRef(true);
  useEffect(() => {
    isOpenRef.current = true;
    return () => {
      isOpenRef.current = false;
    };
  }, []);

  const handleCropComplete = useCallback((_visible: unknown, pixels: PixelCrop) => setArea(pixels), []);

  const handleConfirm = async (): Promise<void> => {
    if (decoded.status !== 'ready' || !area) return;
    setIsWorking(true);
    setFailure(null);
    try {
      const cropped = await cropImageToFile(decoded.image, area, spec, file);
      if (isOpenRef.current) onConfirm(cropped);
    } catch {
      if (!isOpenRef.current) return;
      setFailure(S.FAILED);
      setIsWorking(false);
    }
  };

  // Closing is blocked while the crop is being written, so there is no half-done state to cancel.
  const handleCancel = (): void => {
    if (!isWorking) onCancel();
  };

  const message = decoded.status === 'error' ? decoded.message : failure;
  const canConfirm = decoded.status === 'ready' && area !== null && !isWorking;

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && handleCancel()}
      title={S.TITLE}
      description={S.DESCRIPTION}
      size="lg"
      footer={
        <>
          <Button type="button" variant="green" disabled={!canConfirm} onClick={() => void handleConfirm()}>
            {isWorking ? S.WORKING : S.CONFIRM}
          </Button>
          <Button type="button" variant="outline" disabled={isWorking} onClick={handleCancel}>
            {S.CANCEL}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          role="group"
          aria-label={S.STAGE_LABEL}
          className="relative h-72 w-full overflow-hidden rounded-card bg-navy"
          data-testid="crop-stage"
        >
          {decoded.status === 'ready' && (
            <Cropper
              image={decoded.src}
              crop={crop}
              zoom={zoom}
              minZoom={CROP_ZOOM.MIN}
              maxZoom={CROP_ZOOM.MAX}
              aspect={spec.aspect}
              cropShape={spec.shape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>
        <label className="flex items-center gap-3 text-sm text-text-secondary">
          <span className="shrink-0">{S.ZOOM_LABEL}</span>
          <RangeSlider
            aria-label={S.ZOOM_LABEL}
            aria-valuetext={`${Math.round(zoom * 100)}%`}
            value={zoom}
            min={CROP_ZOOM.MIN}
            max={CROP_ZOOM.MAX}
            step={CROP_ZOOM.STEP}
            disabled={decoded.status !== 'ready' || isWorking}
            onValueChange={setZoom}
          />
        </label>
        {message && (
          <p role="alert" className="text-xs text-danger">
            {message}
          </p>
        )}
      </div>
    </Dialog>
  );
}
